import Image from 'next/image'
import cn from 'classnames'
import CheckMark from './assets/covered-check-mark.png'
import RunnerCard from './assets/runner-card.png'
import Sorry from './assets/sorry.png'
import s from './styles.module.css'
import { ArrowRightIcon } from 'components/design-system/kickoff-icons-v2/arrow-right-icon'
import { WaitlistPhoneNumberForm } from './waitlist-phone-number-form'
import { STEPS } from 'components/sign-up/insurance-coverage-flow/constants'
import Aetna from './assets/aetna-card.png'
import United from './assets/united-card.png'
import BlueCross from './assets/blue-cross-card.png'
import Waitlist from './assets/waitlist.png'
import { useState } from 'react'
import useIsReactNativeWebView from 'hooks/use-is-react-native-webview'
import { CoverageForm } from '../coverage-form'
import {
  getWaitlistContentText,
  WAITLIST_STEPS,
} from 'components/insurance-coverage/waitlist-copy'
import InsurancePaymentGuaranteeCard from 'components/payment/common/insurance-payment-guarantee-card'
import TrackingConsentDisclosure from 'components/shared/tracking-consent-disclosure'

const getInsuranceCompanyImage = (insuranceCompany?: string) => {
  const includesAetna = insuranceCompany?.includes('Aetna')
  const includesUnited = insuranceCompany?.includes('United')
  const includesBlueCross =
    insuranceCompany?.includes('BlueCross') ||
    insuranceCompany?.includes('Blue Cross')
  const includesBlueShield =
    insuranceCompany?.includes('BlueShield') ||
    insuranceCompany?.includes('Blue Shield')

  if (includesAetna) {
    return Aetna
  } else if (includesUnited) {
    return United
  } else if (includesBlueCross) {
    return BlueCross
  } else if (includesBlueShield) {
    return BlueCross
  } else {
    return null
  }
}

interface CoverageResultProps {
  client: any
  insuranceCompany?: string
  eligibilityStatus: string
  onContinue: () => void
  eligibilityData: any
  policyMetadata: any
  copay: number
  showMemberIdSkip: boolean
  // False on an early coverage preview, which has no member ID or diagnoses
  // yet: no co-pay is promised until the full check.
  showCopay?: boolean
  // Set on an inline landing-page variant's sign-up (?lp=v1|v2): the preview
  // greets the client by name and the full result leads into coach matching.
  // Today's flow leaves it unset, so its screens are unchanged.
  variantCopy?: VariantCopy
  referredByCoachEligible: boolean
  // Defaults to the client's saved referral. Flows that also read a
  // referredByCoachId query param pass their combined signal.
  isReferredByCoach?: boolean
  checkEligibility: (overrideData?: any) => Promise<void>
  isLoading?: boolean
}

export interface VariantCopy {
  firstName?: string | null
  carrier?: string | null
  stateName?: string | null
}

/** The coverage result screen. Sign-up's early preview passes `showCopay={false}`. */
export default function CoverageResult({
  client,
  insuranceCompany,
  onContinue,
  eligibilityStatus,
  eligibilityData,
  policyMetadata,
  copay,
  showMemberIdSkip,
  showCopay = true,
  variantCopy,
  referredByCoachEligible,
  isReferredByCoach = !!client?.referredByCoachId,
  checkEligibility,
  isLoading = false,
}: CoverageResultProps) {
  const [showWaitlistSuccess, setShowWaitlistSuccess] = useState(false)
  const isRNWebView = useIsReactNativeWebView()

  const handleCloseWebView = () => {
    if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
      ;(window as any).ReactNativeWebView.postMessage(
        JSON.stringify({ name: 'closeWebView' })
      )
    }
  }

  const handleWaitlistSubmit = () => {
    setShowWaitlistSuccess(true)
  }

  if (eligibilityStatus === STEPS.ERROR) {
    return (
      <div className={s.container}>
        <div className={s.initialContent}>
          <Image src={Sorry} alt="Sorry" width={60} height={60} />
          <h2 className={s.header}>We're sorry.</h2>
        </div>
        <div className={s.content}>
          <p className={s.contentText}>
            There seems to be an issue on your insurance provider's side. We
            will be in touch with you shortly.
          </p>
        </div>
      </div>
    )
  }

  if (eligibilityStatus === STEPS.POLICY_INVALID) {
    const src = getInsuranceCompanyImage(insuranceCompany)
    return (
      <div className={s.container}>
        <div className={s.initialContent}>
          <Image src={Sorry} alt="More Info" width={60} height={60} />
          <h2 className={s.header}>Policy not found</h2>
        </div>
        <div className={s.content}>
          <p className={s.contentText}>
            Please double check the information you submitted and try again.
          </p>
          {policyMetadata?.errors?.length > 0 && (
            <p className={cn(s.contentText, s.errorText)}>
              {policyMetadata?.errors
                ?.map((error: any) => error.message)
                .join(', ')}
            </p>
          )}
          <CoverageForm
            hasErrors={policyMetadata?.errors?.length > 0}
            showMemberIdSkip={showMemberIdSkip}
            checkEligibility={checkEligibility}
            isLoading={isLoading}
            client={client}
          />
          {insuranceCompany && src && (
            <div className={s.insuranceCompanyContainer}>
              <Image
                src={src}
                alt="Insurance Company"
                width={345}
                height={216}
                className={s.insuranceImage}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  if (
    WAITLIST_STEPS.includes(eligibilityStatus) ||
    (isReferredByCoach && !referredByCoachEligible)
  ) {
    if (showWaitlistSuccess) {
      return (
        <div className={s.container}>
          <div className={s.initialContent}>
            <Image src={Waitlist} alt="Waitlist" width={75} height={68} />
            <h2 className={s.headerSuccess}>You're on the waitlist!</h2>
          </div>
          <div className={s.content}>
            <p className={s.contentText}>
              We'll text you as soon as we can get you started with your
              insurance! We're adding coverage every day.
            </p>
            {isRNWebView && (
              <button
                type="button"
                className={s.closeWebViewButton}
                onClick={handleCloseWebView}>
                Close
              </button>
            )}
          </div>
        </div>
      )
    }

    const contentText = getWaitlistContentText({
      eligibilityStatus,
      isReferredByCoach,
    })

    return (
      <div className={s.container}>
        <div className={s.initialContent}>
          <Image src={Sorry} alt="Sorry" width={60} height={60} />
          <h2 className={s.header}>We're sorry.</h2>
        </div>
        <div className={s.content}>
          <p className={s.contentText}>{contentText}</p>
          <p className={s.contentText}>
            {isReferredByCoach
              ? `Enter your phone number and we'll let you know when that changes.`
              : `Enter your phone number and we'll let you know when we have a coach for you.`}
          </p>
          {client && (
            <div className={s.phoneNumberForm}>
              <WaitlistPhoneNumberForm
                clientId={client.id}
                phoneNumber={client.phoneNumber}
                onSubmit={handleWaitlistSubmit}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  const variantPreview = variantCopy && !showCopay
  return (
    <div className={s.container}>
      <div className={cn(s.initialContent, s.initialContentAnimation)}>
        <Image src={CheckMark} alt="Check Mark" width={80} height={80} />
        <h2 className={s.header}>
          {variantPreview
            ? variantCopy.firstName
              ? `Great news, ${variantCopy.firstName}!`
              : 'Great news!'
            : "You're covered!"}
        </h2>
      </div>
      <div className={cn(s.content, s.contentAnimation)}>
        {variantPreview && (
          <>
            <p className={s.contentText}>
              {variantCopy.carrier || 'Your insurance'} typically covers Kickoff
              {variantCopy.stateName ? ` in ${variantCopy.stateName}` : ''}.
            </p>
            <p className={s.contentText}>
              Just a few more questions to confirm your coverage and see if you
              qualify for a $0 co-pay.
            </p>
          </>
        )}
        {showCopay && (
          <p className={s.contentText}>
            Your initial co-pay will be $
            {copay % 1 === 0 ? copay : copay.toFixed(2)}
            {variantCopy && '.'}
          </p>
        )}
        {showCopay && variantCopy && (
          <p className={s.contentText}>Let's match you with your coach.</p>
        )}
        {/* Requirement #6 of the copay settlement plan, phrased as a
            time-bounded guarantee (Nourish-shaped): every word is enforced
            by the settlement engine — the notify-before-charge gate keeps
            increases un-charged until the copay-change email has sent. */}
        {showCopay && (
          <InsurancePaymentGuaranteeCard
            className={s.guaranteeCard}
            text="You won't pay more than this for your session — and if your copay changes for any reason in the future, you will be immediately notified before your next session."
          />
        )}
        <div className={s.imageContainer}>
          <Image
            src={RunnerCard}
            alt="Runner Card"
            width={353}
            height={200}
            className={s.image}
          />
          <button className={s.button} onClick={onContinue}>
            Continue <ArrowRightIcon strokeWidth={2} width={13} height={7} />
          </button>
        </div>
        <TrackingConsentDisclosure />
      </div>
    </div>
  )
}
